/**
 * ShelfMapDrawer – location details drawer for the shelf map.
 *
 * Issue: #217 – CORE – Warehouse Shelf Map – Frontend Location Details Drawer
 *
 * Opens when a map slot is selected and shows:
 *   - Location name
 *   - Item table: LIN, NSN, nomenclature, size, quantity
 *   - Loading / empty / error states
 *   - Closes when the user presses the X or selects another location (parent controls)
 */

import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Skeleton, Stack, Typography, Alert } from '@mui/material';
import { Close, Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useShelfMapLocationInventory } from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import type { ShelfMapInventoryItem } from '@/services/shelfMapService';

// ---------------------------------------------------------------------------
// InventoryItemBlocks — 3-column display with expand/collapse
// ---------------------------------------------------------------------------

const COLLAPSED_ROWS = 3;

interface InventoryItemBlocksProps {
  items: ShelfMapInventoryItem[];
  expanded: boolean;
  onToggleExpand: () => void;
}

const InventoryItemBlocks: React.FC<InventoryItemBlocksProps> = ({
  items,
  expanded,
  onToggleExpand,
}) => {
  const hasMore = items.length > COLLAPSED_ROWS;
  const visibleItems = expanded || !hasMore ? items : items.slice(0, COLLAPSED_ROWS);

  return (
    <Box>
      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr auto',
          gap: 1,
          mb: 0.5,
          px: 0.5,
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          LIN / NSN
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Nomenclature
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textAlign: 'right', minWidth: 32 }}
        >
          Qty
        </Typography>
      </Box>
      <Divider sx={{ mb: 0.5 }} />

      {/* Visible rows */}
      <Stack spacing={0}>
        {visibleItems.map((item, idx) => (
          <Box
            key={`${item.catalog_id ?? ''}-${item.size ?? ''}-${idx}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto',
              gap: 1,
              px: 0.5,
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 0 },
            }}
          >
            {/* Block 1: LIN + NSN */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                fontFamily="monospace"
                display="block"
                noWrap
                title={item.lin || undefined}
              >
                {item.lin || '—'}
              </Typography>
              <Typography
                variant="caption"
                fontFamily="monospace"
                color="text.secondary"
                display="block"
                noWrap
                title={item.nsn || undefined}
                sx={{ fontSize: 10 }}
              >
                {item.nsn || '—'}
              </Typography>
            </Box>

            {/* Block 2: Nomenclature */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                display="block"
                title={item.nomenclature || undefined}
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.nomenclature || '—'}
              </Typography>
              {item.size && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {item.size}
                </Typography>
              )}
            </Box>

            {/* Block 3: Quantity */}
            <Box sx={{ textAlign: 'right', minWidth: 32 }}>
              <Typography variant="caption" fontWeight={700}>
                {item.quantity}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Expand / collapse trigger */}
      {hasMore && (
        <Button
          size="small"
          onClick={onToggleExpand}
          startIcon={expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          sx={{ mt: 0.5, fontSize: 12 }}
          variant="text"
          color="inherit"
        >
          {expanded ? 'Show less' : `Show ${items.length - COLLAPSED_ROWS} more`}
        </Button>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ShelfMapDrawerProps {
  /** The layout the selected slot belongs to. */
  layoutId: string | null;
  /** The location entity ID for the selected slot. */
  locationEntityId: string | null;
  /** Display label for the selected slot (e.g. location_name like "2AZ"). */
  slotLabel?: string | null;
  /** Full breadcrumb path for the location (e.g. "Warehouse / 1E / 2AZ"). */
  slotBreadcrumb?: string | null;
  /** Aisle label for the selected slot (e.g. "1E"). */
  aisleLabel?: string | null;
  /** Controls drawer visibility. */
  open: boolean;
  /** Called when the drawer should be closed. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ShelfMapDrawer: React.FC<ShelfMapDrawerProps> = ({
  layoutId,
  locationEntityId,
  slotLabel,
  slotBreadcrumb,
  aisleLabel,
  open,
  onClose,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Reset expand state when location changes
  useEffect(() => {
    setExpanded(false);
  }, [locationEntityId]);

  const {
    data: inventory,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useShelfMapLocationInventory(open ? layoutId : null, open ? locationEntityId : null);

  // Show skeleton when fetching data for a *different* location than what's
  // currently displayed (rapid slot clicks). inventory.location_id tracks
  // which location the cached response belongs to.
  const isDataStale = isFetching && inventory?.location_id !== locationEntityId;
  const showLoading = (locationEntityId && isLoading) || isDataStale;

  // Header shows current slot's label immediately; falls back to inventory
  // location_name once the response arrives (they should match).
  const locationName = showLoading
    ? (slotLabel ?? 'Loading\u2026')
    : (inventory?.location_name ?? slotLabel ?? 'Selected Location');
  const items = !showLoading && inventory?.items ? inventory.items : [];
  // Breadcrumb title: prefer passed breadcrumb, otherwise fall back to location name.
  const drawerTitle = slotBreadcrumb ?? locationName;
  // Subtitle: "Row: {aisle} • Shelf: {locationName}" when both are available.
  const drawerSubtitle =
    aisleLabel && locationName && !showLoading
      ? `Row: ${aisleLabel} • Shelf: ${locationName}`
      : null;
  // Rendered as an inline flex child of the map's flex row so it sits alongside
  // the SVG rather than in a Portal overlay (which would cover the map and block
  // pointer events). display:none keeps it mounted so React Query cache survives.
  return (
    <Box
      sx={{
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        width: { xs: '100%', sm: 456 },
        flexShrink: 0,
        borderLeft: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            title={drawerTitle}
            sx={{ fontSize: '0.9rem' }}
          >
            {drawerTitle}
          </Typography>
          {drawerSubtitle && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {drawerSubtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* No location linked */}
        {!locationEntityId && (
          <Alert severity="info">
            This slot has no linked location. Assign a location to view inventory.
          </Alert>
        )}

        {/* Loading skeleton (first load or slot change) */}
        {locationEntityId && showLoading && (
          <Stack spacing={1}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        )}

        {/* Error state */}
        {locationEntityId && isError && !showLoading && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<Refresh fontSize="small" />}
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            }
          >
            Failed to load inventory for this location.
          </Alert>
        )}

        {/* Empty state */}
        {locationEntityId && !showLoading && !isError && items.length === 0 && (
          <Alert severity="info">No on-hand inventory at this location.</Alert>
        )}

        {/* Item blocks — 3-column layout: LIN+NSN | Nomenclature | Quantity */}
        {locationEntityId && !showLoading && !isError && items.length > 0 && (
          <InventoryItemBlocks
            items={items}
            expanded={expanded}
            onToggleExpand={() => setExpanded((v) => !v)}
          />
        )}
      </Box>

      {/* Close button footer */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<Close fontSize="small" />}
          onClick={onClose}
          aria-label="Close details drawer"
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};
